import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import Grid from '@mui/material/Grid';
import {
  Box, FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { FilterContext } from 'shared/hoc/withFilters';
import { Title } from 'shared/components/abstract/interactables';
import SplitButton from 'shared/components/SplitButton';
import { VARIANTS } from 'shared/constants';
import useDataTable, { flattenFilters } from './useDataTable';
import StickyHeadTable, { getDefaultRowClassName } from './StickyHeadTable';
import ExportButton from './ExportButton';
import PageControls from './PageControls';
import FilterModal from './filters';
import FilterChips from './FilterChips';

const limitOptions = [100, 200, 300, 400, 500];

/**
 * @param {Object} props
 * @param {string} [props.baseSearch=''] - Pass this to the searching service along with
 * additional filters
 * @param {Array<*>} props.columns
 * @param {number} [props.defaultLimit=100]
 * @param {Array<Object>} [props.filters]
 * @param {string} [props.filterKey='']
 * @param {Function} [props.getRowClassName=getDefaultRowClassName]
 * @param {Array<string>} [props.ignoredKeys=[]] - Keys that aren't passed to backend
 * @param {string} [props.noRowsText='Results not found']
 * @param {string} props.rowId
 * @param {Function} props.searchRequest - Examples in client/src/services directory
 * @param {boolean} [props.noExportButton=false]
 * @param {boolean} [props.noSearchForm=false]
 * @param {string} [props.placeholder='Enter to search']
 * @param {string} [props.tableName='']
 * @param {number} [props.updateCount=0] - Changing this forces table to reload
 * @param {string} [props.className='default-table-height']
 * @param {string} [props.cellStyling='']
 */
export default function DataTable(props) {
  const {
    baseSearch = '',
    columns,
    defaultLimit = limitOptions[0],
    filters,
    filterKey = '',
    getRowClassName = getDefaultRowClassName,
    ignoredKeys = [],
    noRowsText = 'Results not found',
    rowId,
    searchRequest,
    noExportButton = false,
    noSearchForm = false,
    placeholder = 'Enter to search',
    tableName = '',
    updateCount = 0,
    className = 'default-table-height',
    cellStyling = '',
  } = props;
  const { clearFavoriteFilters } = useContext(FilterContext);

  const flatFilters = useMemo(() => flattenFilters(filters), [filters]);

  const {
    clearTable,
    exportCSV,
    isLoading,
    onReorder,
    pageBack,
    pageFirst,
    pageForward,
    pageLast,
    search,
    setFilters,
    removeFilterChip,
    setLimit,
    tableState,
  } = useDataTable({
    baseSearch,
    defaultLimit,
    filters,
    ignoredKeys,
    searchRequest,
    updateCount,
  });

  const {
    filterState,
    availablePages,
    defaultSearch,
    rows,
    total,
  } = tableState;

  const {
    // Filters all datatables share.
    query,
    limit,
    page,
    order,
    orderBy,
    // Filters that are configured from props and set in the modal.
    ...customFilterState
  } = filterState;
  const [dirtyQuery, setDirtyQuery] = useState('');

  // Update the state of the search box when q changes.
  useEffect(() => {
    setDirtyQuery(query);
  }, [query]);

  // Make sure the current value of limit appears in the dropdown.
  const limitSet = new Set(limitOptions);
  limitSet.add(defaultLimit);
  if (limit) limitSet.add(limit);

  const placeHolderBox = (label) => (
    <Box sx={{ fontSize: '1rem' }}>
      {label}
    </Box>
  );

  return (
    <Grid container rowSpacing={1}>
      <Grid size={12}>
        {tableName && <Title title={tableName} />}
      </Grid>

      {/* Data Count and Modifier Displays */}
      <Grid container size={12} sx={{ justifyContent: 'space-between' }}>
        {filters && filters.length > 0 && (
          <Grid size={{ xs: 12, md: 9 }}>
            <FilterChips
              filters={flatFilters}
              filterState={filterState}
              removeFilterChip={removeFilterChip}
            />
          </Grid>
        )}
        <Grid sx={{ justifyItems: 'end' }}>
          <div className="item-child-subtitle">
            {`${total} Total Results`}
          </div>
        </Grid>
      </Grid>

      {/* Data Filters and Controls */}
      <Grid container size={12} sx={{ justifyContent: 'space-between' }}>
        <Grid
          container
          size={{ xs: 12, lg: 6, xl: 7 }}
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          {!noSearchForm && searchRequest && (
            <Grid size={{
              xs: 12, sm: 7, lg: 12, xl: 8,
            }}
            >
              <form onSubmit={(e) => {
                e.preventDefault();
                setFilters([['q', dirtyQuery], ['p', '']]);
              }}
              >
                <TextField
                  onChange={({ target: { value } }) => setDirtyQuery(value)}
                  disabled={isLoading}
                  label={dirtyQuery === '' ? placeHolderBox(placeholder) : ''}
                  type="search"
                  value={dirtyQuery}
                  size="small"
                  sx={{ backgroundColor: 'inputBackground' }}
                  slotProps={{
                    inputLabel: {
                      fontSize: '1rem !important',
                      shrink: false,
                    },
                  }}
                  fullWidth
                />
              </form>
            </Grid>
          )}
          <Grid
            container
            size={{
              xs: 12, sm: 5, lg: 12, xl: 4,
            }}
            spacing={1.5}
            sx={{
              justifyContent: {
                xs: 'end',
                sm: 'space-between',
                lg: 'end',
                xl: 'start',
              },
            }}
          >
            {filters && filters?.length > 0 && (
              <Grid sx={{ alignContent: 'center' }}>
                <FilterModal
                  setFilters={setFilters}
                  filters={filters}
                  filterKey={filterKey}
                  filterState={customFilterState}
                  tableName={tableState.dataTitle || tableName}
                  search={search}
                />
              </Grid>
            )}
            {(!noSearchForm || (filters && filters?.length > 0)) && (
              <Grid sx={{ alignContent: 'center' }}>
                <SplitButton
                  title="Clear"
                  onClick={clearTable}
                  color={VARIANTS.primary}
                  variant={VARIANTS.outlined}
                  disabled={!search || search === defaultSearch}
                  menuItems={[{
                    menuItemClick: () => clearFavoriteFilters(filterKey),
                    menuItemTitle: 'Clear Favorite Filters',
                  }]}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          size={{ xs: 12, lg: 6, xl: 4 }}
          spacing={2}
          sx={{ justifyContent: 'flex-end' }}
        >
          {(availablePages > 1 || rows?.length > defaultLimit) && (
            <>
              <Grid size="fit-content">
                <PageControls
                  availablePages={availablePages}
                  disabled={isLoading}
                  page={page}
                  pageBack={pageBack}
                  pageFirst={pageFirst}
                  pageForward={pageForward}
                  pageLast={pageLast}
                />
              </Grid>
              <Grid size="fit-content">
                <FormControl fullWidth>
                  <InputLabel>Limit</InputLabel>
                  <Select
                    label="Limit"
                    className="limit-select"
                    disabled={isLoading}
                    onChange={({ target: { value } }) => setLimit(value)}
                    value={limit || defaultLimit}
                    fullWidth
                    variant="outlined"
                  >
                    {Array
                      .from(limitSet)
                      .sort((a, b) => a - b)
                      .map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      {/* Table */}
      <Grid container size={12}>
        <StickyHeadTable
          columns={columns}
          currentOrder={order}
          currentOrderBy={orderBy}
          getRowClassName={getRowClassName}
          noRowsText={noRowsText}
          onReorder={onReorder}
          rowId={rowId}
          rows={rows}
          search={search}
          isLoading={isLoading}
          striped
          className={className}
          cellStyling={cellStyling}
        />
      </Grid>

      {/* Export Button */}
      <Grid container size={12} sx={{ justifyContent: 'end' }}>
        {!noExportButton && (
          <Grid>
            <ExportButton
              onExport={exportCSV}
              disabled={isLoading}
              hidden={!rows || rows?.length === 0}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
